import { useEffect, useState } from "react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DangerAlert, WarningAlert } from "@/components/shared/Alerts";
import { useStructure } from "@/hooks/useDesignXML";
import type { Structure } from "@/types/designs";
import type { StructureSummary } from "@/types/structure";

import { OpticalPortsTable } from "./tables/OpticalPortsTable";
import { PortCombosTable } from "./tables/PortCombosTable";
import { ProbePadsTable } from "./tables/ProbePadsTable";

const overviewTabs = [
  { value: "optical-ports", label: "Optical Ports" },
  { value: "port-combos", label: "Port Combos" },
  { value: "probe-pads", label: "Probe Pads" },
] as const;

type OverviewTabValue = (typeof overviewTabs)[number]["value"];

interface DesignOverviewDialogProps {
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  selectedStructure: StructureSummary | null;
}

export function DesignOverviewDialog({
  open,
  onOpenChange,
  selectedStructure,
}: DesignOverviewDialogProps) {
  const [activeTab, setActiveTab] = useState<OverviewTabValue>("optical-ports");
  const [isEditing, setIsEditing] = useState(false);
  const [deviceClassDraft, setDeviceClassDraft] = useState("");
  const [isReferenceWaveguideDraft, setIsReferenceWaveguideDraft] = useState(false);
  const [showPortComboDefaults, setShowPortComboDefaults] = useState(false);

  const {
    data: structureOverview,
    error: structureOverviewError,
    isLoading: isStructureOverviewLoading,
  } = useStructure(selectedStructure?.id);

  useEffect(() => {
    setActiveTab("optical-ports");
    setIsEditing(false);
    setShowPortComboDefaults(false);
  }, [selectedStructure?.id, open]);

  useEffect(() => {
    setDeviceClassDraft(structureOverview?.deviceClass ?? "");
    setIsReferenceWaveguideDraft(structureOverview?.isReferenceWaveguide ?? false);
  }, [structureOverview?.deviceClass, structureOverview?.isReferenceWaveguide]);

  const structureName = structureOverview?.name ?? selectedStructure?.name ?? "Structure details";
  const testSiteName = structureOverview?.testSiteName ?? "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden mx-auto flex max-h-[85vh] w-[92vw] max-w-[92vw] flex-col gap-0 p-0 sm:w-[85vw] sm:max-w-[85vw] lg:w-[80vw] lg:max-w-[80vw] xl:w-[75vw] xl:max-w-[75vw]">
        {selectedStructure ? (
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as OverviewTabValue)}
            className="flex min-h-[85vh] flex-col"
          >
            <div className="border-b bg-background px-6 pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                    Structure
                  </p>
                  <h2 className="truncate text-2xl font-semibold">{structureName}</h2>
                  <p className="text-sm truncate text-muted-foreground">
                    ID: {selectedStructure.id}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 rounded-md border bg-muted/20 p-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Test Site Name
                  </p>
                  <p className="text-sm font-medium">{testSiteName}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Device Class
                  </p>
                  {isEditing ? (
                    <Input
                      value={deviceClassDraft}
                      onChange={(event) => setDeviceClassDraft(event.target.value)}
                      className="h-8"
                    />
                  ) : (
                    <p className="text-sm font-medium">{deviceClassDraft || "—"}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Is Reference Waveguide
                  </p>
                  {isEditing ? (
                    <label className="flex h-8 items-center gap-2 text-sm font-medium">
                      <Checkbox
                        checked={isReferenceWaveguideDraft}
                        onCheckedChange={(checked) =>
                          setIsReferenceWaveguideDraft(checked === true)
                        }
                      />
                      <span>{isReferenceWaveguideDraft ? "Yes" : "No"}</span>
                    </label>
                  ) : (
                    <p className="text-sm font-medium">
                      {isReferenceWaveguideDraft ? "Yes" : "No"}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Show Port Combo Defaults
                  </p>
                  <label className="flex h-8 items-center gap-2 text-sm font-medium">
                    <Checkbox
                      checked={showPortComboDefaults}
                      onCheckedChange={(checked) => setShowPortComboDefaults(checked === true)}
                    />
                    <span>{showPortComboDefaults ? "On" : "Off"}</span>
                  </label>
                </div>
              </div>

              <TabsList className="mt-4 w-full justify-start gap-2 rounded-md bg-muted/60 p-1">
                {overviewTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="min-w-[140px] rounded-sm px-3 py-2"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <div className="min-h-0 flex-1 overflow-auto px-6 pt-0 pb-1 [&_[data-slot=table-container]]:min-w-full [&_[data-slot=table-container]]:w-max [&_[data-slot=table-container]]:overflow-visible [&_[data-slot=table]]:min-w-full [&_[data-slot=table]]:w-max [&_[data-slot=table-head]]:sticky [&_[data-slot=table-head]]:top-0 [&_[data-slot=table-head]]:z-10 [&_[data-slot=table-head]]:bg-background">
              {structureOverviewError ? (
                <DangerAlert>{structureOverviewError.message}</DangerAlert>
              ) : isStructureOverviewLoading ? (
                <div className="p-2">Loading structure...</div>
              ) : structureOverview ? (
                <>
                  <TabsContent value="optical-ports" className="mt-0">
                    <OpticalPortsTable
                      structure={structureOverview as Structure}
                      isEditing={isEditing}
                    />
                  </TabsContent>

                  <TabsContent value="port-combos" className="mt-0">
                    <PortCombosTable
                      structure={structureOverview as Structure}
                      isEditing={isEditing}
                      showDefaults={showPortComboDefaults}
                    />
                  </TabsContent>

                  <TabsContent value="probe-pads" className="mt-0">
                    <ProbePadsTable
                      structure={structureOverview as Structure}
                      isEditing={isEditing}
                    />
                  </TabsContent>
                </>
              ) : (
                <WarningAlert>No structure data found.</WarningAlert>
              )}
            </div>
          </Tabs>
        ) : (
          <div className="flex min-h-[60vh] items-center justify-center p-6">
            <WarningAlert>No structure selected.</WarningAlert>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
